import React from 'react';
import { Box, Flex, Stack, Text, IconButton } from '@chakra-ui/react';
import { ArrowLeft } from 'phosphor-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { usePermittedSubmodules } from '../../utils/getSubmodulesForPath';
import { useAuth } from '../../providers/auth';

interface SidebarDrillProps {
  /** Path base do modulo (ex: '/modulesHome/accountingConciliation'). */
  basePath: string;
  /** Nome do modulo a exibir no header. */
  moduleName: string;
  /** Cor representativa do modulo (background do header). */
  moduleColor: string;
  /** Icone do modulo. */
  moduleIcon: React.ReactNode;
  /** Sidebar expandida ou colapsada. */
  isOpen: boolean;
  /** Path de destino do botao "Voltar". Default: '/' (home). */
  backPath?: string;
  /** Texto ao lado do "Voltar" e sobretitulo do card. Default: modulos da home. */
  backLabel?: string;
  /**
   * Submódulos explícitos (Fiscal, Integração — `submodulos.tsx`), na ordem do
   * hub. Sem a lista, são as rotas filhas de `basePath` (Contábil, Financeiro).
   */
  itens?: Array<{ name: string; path: string }>;
}

export const SidebarDrill = ({
  basePath,
  moduleName,
  moduleColor,
  moduleIcon,
  isOpen,
  backPath = '/',
  backLabel,
  itens,
}: SidebarDrillProps) => {
  const location = useLocation();
  const { hasPermission, isAdmin } = useAuth();
  const dasRotas = usePermittedSubmodules(basePath);
  const submodules = itens ? itens.filter(sub => isAdmin() || hasPermission(sub.path)) : dasRotas;
  // Fiscal/Integração (lista explícita): cada tela tem os próprios filtros na
  // URL (ex.: screen_data do Simples) — levar para outra tela quebraria a dela.
  const search = itens ? '' : location.search;

  return (
    <Stack spacing={3} px={isOpen ? 3 : 1} pt={2} pb={6}>
      {/* Botao voltar */}
      <Flex align="center" gap={2}>
        <IconButton
          aria-label={backLabel ? `Voltar para ${backLabel}` : 'Voltar para todos os módulos'}
          icon={<ArrowLeft size={16} weight="bold" />}
          size="sm"
          variant="ghost"
          color="#71717A"
          as={RouterLink}
          to={backPath}
          _hover={{ bg: 'rgba(27, 136, 245, 0.1)', color: '#1B88F5' }}
        />
        {isOpen && (
          <Text fontSize="11px" color="#71717A" textTransform="uppercase" fontWeight="medium">
            {backLabel ?? 'Módulos'}
          </Text>
        )}
      </Flex>

      {/* Card destaque do modulo (clicavel - volta pra home do modulo) */}
      <Box
        as={RouterLink}
        to={{ pathname: basePath, search }}
        display="block"
        textAlign="left"
        width="100%"
        bg={moduleColor}
        borderRadius="2xl"
        p={isOpen ? 4 : 2}
        borderWidth="2px"
        borderColor={location.pathname === basePath ? '#1B88F5' : 'rgba(0, 0, 0, 0.05)'}
        boxShadow="0 4px 14px 0 rgba(0, 0, 0, 0.05)"
        cursor="pointer"
        aria-current={location.pathname === basePath ? 'page' : undefined}
        aria-label={`Abrir ${moduleName}`}
        transition="all 0.18s ease"
        _hover={{
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.08)',
          borderColor: '#1B88F5',
        }}
        _focusVisible={{ outline: '2px solid', outlineColor: '#1B88F5', outlineOffset: '2px' }}
      >
        <Flex align="center" gap={3} direction={isOpen ? 'row' : 'column'}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            p={3}
            bg="white"
            flexShrink={0}
            boxShadow="0 2px 8px rgba(0,0,0,0.08)"
          >
            {moduleIcon}
          </Box>
          {isOpen && (
            <Box flex={1} minW={0}>
              <Text
                fontSize="9px"
                color="#71717A"
                textTransform="uppercase"
                fontWeight="semibold"
                letterSpacing="0.05em"
              >
                {backLabel ?? 'Módulo'}
              </Text>
              <Text fontSize="13px" fontWeight="bold" color="#0F172A" lineHeight="1.2" mt={0.5}>
                {moduleName}
              </Text>
            </Box>
          )}
        </Flex>
      </Box>

      {isOpen && (
        <Text
          fontSize="10px"
          color="#71717A"
          textTransform="uppercase"
          fontWeight="semibold"
          letterSpacing="0.05em"
          mt={2}
          ml={1}
        >
          Submódulos
        </Text>
      )}

      {/* Lista de submodulos */}
      <Stack spacing={2}>
        {submodules.length === 0 && isOpen && (
          <Text fontSize="11px" color="#94A3B8" textAlign="center" py={4}>
            Nenhum submódulo disponível
          </Text>
        )}

        {submodules.map(sub => {
          // Telas internas do submodulo (detalhe, edicao) mantem o item aceso.
          const isActive =
            location.pathname === sub.path || location.pathname.startsWith(`${sub.path}/`);
          // Preserva a querystring (ex.: empresa/período da Auditoria, filtros do
          // Financeiro) ao trocar de submódulo — senão o contexto da topbar global
          // se perde a cada navegação. Link de verdade: botão direito / Ctrl+clique
          // abrem em nova guia.
          return (
            <Box
              key={sub.path}
              as={RouterLink}
              to={{ pathname: sub.path, search }}
              display="block"
              aria-current={isActive ? 'page' : undefined}
              cursor="pointer"
              px={3}
              py={2.5}
              borderRadius="lg"
              bg={isActive ? 'rgba(27, 136, 245, 0.10)' : 'transparent'}
              borderWidth="1px"
              borderColor={isActive ? '#1B88F5' : 'transparent'}
              transition="all 0.18s ease"
              _hover={{ bg: 'rgba(27, 136, 245, 0.05)' }}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: '#1B88F5',
                outlineOffset: '2px',
              }}
            >
              <Flex align="center" gap={2}>
                <Box
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={isActive ? '#1B88F5' : '#94A3B8'}
                  flexShrink={0}
                  transition="all 0.18s ease"
                />
                {isOpen && (
                  <Text
                    fontSize="12px"
                    fontWeight={isActive ? 'semibold' : 'medium'}
                    color={isActive ? '#1B88F5' : '#0F172A'}
                    isTruncated
                  >
                    {sub.name}
                  </Text>
                )}
              </Flex>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
